import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import CookieManager from '../lib/cookieManager';
import { useLanguage } from '../context/LanguageContext';

const CookieBanner = () => {
  const { t } = useLanguage();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Always true, can't be disabled
    functional: false,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made cookie choices
    const savedPreferences = localStorage.getItem('cookiePreferences');
    const cookieConsent = localStorage.getItem('cookieConsent');
    
    if (!savedPreferences && !cookieConsent) {
      // schedule to avoid react-hooks/set-state-in-effect lint warning
      setTimeout(() => setShowBanner(true), 0);
    } else if (savedPreferences) {
      // schedule to avoid react-hooks/set-state-in-effect lint warning
      setTimeout(() => setCookiePreferences(JSON.parse(savedPreferences)), 0);
    }
  }, []);

  const acceptAllCookies = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    
    setCookiePreferences(allAccepted);
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    localStorage.setItem('cookieConsent', 'accepted');
    CookieManager.onPreferenceChange(); // Update cookie management
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptNecessaryOnly = () => {
    const necessaryOnly = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    };
    
    setCookiePreferences(necessaryOnly);
    localStorage.setItem('cookiePreferences', JSON.stringify(necessaryOnly));
    localStorage.setItem('cookieConsent', 'partial');
    CookieManager.onPreferenceChange(); // Update cookie management
    setShowBanner(false);
    setShowSettings(false);
  };

  const saveCustomPreferences = () => {
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    localStorage.setItem('cookieConsent', 'custom');
    CookieManager.onPreferenceChange(); // Update cookie management
    setShowBanner(false);
    setShowSettings(false);
  };

  const handlePreferenceChange = (category) => {
    if (category === 'necessary') return; // Can't disable necessary cookies
    
    setCookiePreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Backdrop (visual only; allow language switch / navigation) */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 pointer-events-none" />
      
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-gray-900 via-black to-gray-900 border-t border-cyan-400/30 shadow-2xl">
        <div className="max-w-7xl mx-auto p-6">
          {!showSettings ? (
            // Main Banner
            <div className="text-center">
              <div className="flex items-center justify-center mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full flex items-center justify-center mr-3">
                  <span className="text-white text-sm">🍪</span>
                </div>
                <h3 className="text-xl font-bold text-white">{t('cookieBanner.title')}</h3>
              </div>
              
              <p className="text-gray-300 mb-6 max-w-4xl mx-auto leading-relaxed">
                {t('cookieBanner.description')}{' '}
                <a href="/privacy" className="text-cyan-300 hover:text-cyan-200 underline">
                  {t('cookieBanner.privacyLink')}
                </a>{' '}
                {t('cookieBanner.moreInfo')}
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                <Button
                  onClick={acceptAllCookies}
                  className="group relative bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-8 py-3 rounded-full font-semibold transform hover:scale-105 transition-all duration-300 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-lg"></div>
                  <span className="relative z-10">{t('cookieBanner.acceptAll')}</span>
                </Button>
                
                <Button
                  onClick={acceptNecessaryOnly}
                  variant="outline"
                  className="bg-transparent border-2 border-gray-400 text-gray-300 hover:bg-gray-800 hover:border-gray-300 px-8 py-3 rounded-full font-semibold transform hover:scale-105 transition-all duration-300"
                >
                  {t('cookieBanner.necessaryOnly')}
                </Button>
                
                <Button
                  onClick={() => setShowSettings(true)}
                  variant="ghost"
                  className="text-cyan-300 hover:text-cyan-200 hover:bg-cyan-500/10 px-6 py-3 rounded-full transition-all duration-300"
                >
                  {t('cookieBanner.customize')}
                  
                </Button>
              </div>
            </div>
          ) : (
            // Cookie Settings
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-white flex items-center">
                  <span className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mr-3 text-sm">⚙️</span>
                  {t('cookieBanner.settingsTitle')}
                </h3>
                <Button
                  onClick={() => setShowSettings(false)}
                  variant="ghost"
                  className="text-gray-400 hover:text-white p-2"
                >
                  ✕
                </Button>
              </div>

              <div className="grid gap-4 mb-6">
                {/* Necessary Cookies */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-lg font-semibold text-white">{t('cookieBanner.categories.necessary.title')}</h4>
                    <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm">{t('cookieBanner.enabled')}</div>
                  </div>
                  <p className="text-gray-400 text-sm">
                    {t('cookieBanner.categories.necessary.description')}
                  </p>
                </div>

                {/* Functional Cookies */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-lg font-semibold text-white">{t('cookieBanner.categories.functional.title')}</h4>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={cookiePreferences.functional}
                        onChange={() => handlePreferenceChange('functional')}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                    </label>
                  </div>
                  <p className="text-gray-400 text-sm">
                    {t('cookieBanner.categories.functional.description')}
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-lg font-semibold text-white">{t('cookieBanner.categories.analytics.title')}</h4>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={cookiePreferences.analytics}
                        onChange={() => handlePreferenceChange('analytics')}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                    </label>
                  </div>
                  <p className="text-gray-400 text-sm">
                    {t('cookieBanner.categories.analytics.description')}
                  </p>
                </div>

                {/* Marketing Cookies */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-lg font-semibold text-white">{t('cookieBanner.categories.marketing.title')}</h4>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={cookiePreferences.marketing}
                        onChange={() => handlePreferenceChange('marketing')}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                    </label>
                  </div>
                  <p className="text-gray-400 text-sm">
                    {t('cookieBanner.categories.marketing.description')}
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={saveCustomPreferences}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-3 rounded-full font-semibold transform hover:scale-105 transition-all duration-300"
                >
                  {t('cookieBanner.save')}
                </Button>
                
                <Button
                  onClick={acceptAllCookies}
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-8 py-3 rounded-full font-semibold transform hover:scale-105 transition-all duration-300"
                >
                  {t('cookieBanner.acceptAllShort')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieBanner;